import type { I18n } from '@lingui/core';
import type { WorkflowItemResponse } from '@/types/workflow';
import { assignedRoleLabel } from '@/types/workflowRoles';

/**
 * Who has to act on a workflow item, in the order the inbox answers it:
 *
 *   person  a person holds it (`assigned_to`);
 *   lead    nobody holds it, but the item is about a child whose group has a
 *           lead: the lead is responsible for their group's items;
 *   role    nobody holds it; it waits for anyone with the role;
 *   nobody  nothing says who.
 */
export type Responsible =
  | { kind: 'person'; name: string; clerkUserId: string; isMe: boolean }
  | { kind: 'lead'; name: string; employeeId: string; clerkUserId: string; groupName: string }
  | { kind: 'role'; role: string }
  | { kind: 'nobody' };

export interface ResponsibleContext {
  /** The current user's Clerk id, for "you". */
  userId?: string | null;
  /** The current user is an org admin (for what they may do with the item). */
  isAdmin?: boolean;
  /** Clerk user id -> display name (the any-module employee picker). */
  namesByClerkUserId: ReadonlyMap<string, string>;
  /** Printed for a person the picker cannot name. */
  unknownName: string;
}

/** Resolves who is responsible for the item. */
export function responsibleFor(
  item: Pick<
    WorkflowItemResponse,
    'assigned_to' | 'assigned_role' | 'admin_only' | 'group_lead_employee_id' | 'group_lead_name' | 'group_lead_clerk_user_id' | 'group_name'
  >,
  ctx: ResponsibleContext,
): Responsible {
  if (item.assigned_to) {
    return {
      kind: 'person',
      clerkUserId: item.assigned_to,
      name: ctx.namesByClerkUserId.get(item.assigned_to) ?? (item.assigned_to === ctx.userId ? '' : ctx.unknownName),
      isMe: item.assigned_to === ctx.userId,
    };
  }
  // An admin-only item is not the lead's to handle: it waits for the role.
  if (!item.admin_only && item.group_lead_employee_id && item.group_lead_name) {
    return {
      kind: 'lead',
      name: item.group_lead_name,
      employeeId: item.group_lead_employee_id,
      clerkUserId: item.group_lead_clerk_user_id ?? '',
      groupName: item.group_name ?? '',
    };
  }
  if (item.assigned_role) {
    return { kind: 'role', role: item.assigned_role };
  }
  return { kind: 'nobody' };
}

/**
 * The three-case rule behind the Responsible column, one sentence per case, for
 * the info bubble in the column header.
 */
export function responsibleRuleLines(i18n: Pick<I18n, '_'>): string[] {
  return [
    i18n._({
      id: 'workflow.responsible.rule.person',
      message: 'The person who took the item.',
    }),
    i18n._({
      id: 'workflow.responsible.rule.lead',
      message: "Otherwise the child's group lead, except on items only admins can see.",
    }),
    i18n._({
      id: 'workflow.responsible.rule.role',
      message: 'Otherwise the role the item waits for, for example administration.',
    }),
  ];
}

/** The short name of the responsible: a person's name, or who the item waits for. */
export function responsibleName(i18n: Pick<I18n, '_'>, r: Responsible): string {
  switch (r.kind) {
    case 'person':
      return r.isMe ? i18n._({ id: 'workflow.responsible.you', message: 'You' }) : r.name;
    case 'lead':
      return r.name;
    case 'role':
      return roleQueueLabel(i18n, r.role);
    case 'nobody':
      return i18n._({ id: 'workflow.responsible.nobody', message: 'Nobody yet' });
  }
}

/** The second line under the name: why this person, or who else may act. */
export function responsibleDetail(i18n: Pick<I18n, '_'>, r: Responsible): string | null {
  switch (r.kind) {
    case 'person':
      return r.isMe && r.name ? r.name : null;
    case 'lead':
      return r.groupName
        ? i18n._({ id: 'workflow.responsible.leadOf', message: 'Group lead · {group}', values: { group: r.groupName } })
        : i18n._({ id: 'workflow.responsible.lead', message: 'Group lead' });
    case 'role':
      return i18n._({ id: 'workflow.responsible.notTaken', message: 'Not taken' });
    case 'nobody':
      return null;
  }
}

/** "Any admin", "Any educator": who the item waits for while nobody holds it. */
export function roleQueueLabel(i18n: Pick<I18n, '_'>, role: string): string {
  switch (role) {
    case 'admin':
      return i18n._({ id: 'workflow.responsible.role.admin', message: 'Any admin' });
    case 'educator':
      return i18n._({ id: 'workflow.responsible.role.educator', message: 'Any educator' });
    case 'shift_manager':
      return i18n._({ id: 'workflow.responsible.role.shiftManager', message: 'Shift planning' });
    case 'billing_admin':
      return i18n._({ id: 'workflow.responsible.role.billingAdmin', message: 'Billing' });
    case 'manager':
      return i18n._({ id: 'workflow.responsible.role.manager', message: 'Management' });
    default:
      return assignedRoleLabel(i18n, role) ?? i18n._({ id: 'workflow.responsible.nobody', message: 'Nobody yet' });
  }
}

/**
 * The key the board groups by: one column per person, per group lead, per
 * waiting role, and one for items nobody is named for. "You" sorts first.
 */
export function responsibleGroupKey(r: Responsible): string {
  switch (r.kind) {
    case 'person':
      return r.isMe ? 'me' : `person:${r.clerkUserId}`;
    case 'lead':
      return `lead:${r.employeeId}`;
    case 'role':
      return `role:${r.role}`;
    case 'nobody':
      return 'nobody';
  }
}

/** Sort order of board columns: you, people, group leads, waiting roles, nobody. */
export function responsibleGroupRank(r: Responsible): number {
  switch (r.kind) {
    case 'person':
      return r.isMe ? 0 : 1;
    case 'lead':
      return 2;
    case 'role':
      return 3;
    case 'nobody':
      return 4;
  }
}

/** Two letters for the avatar of a person or lead; empty for a role or nobody. */
export function responsibleInitials(r: Responsible): string {
  if (r.kind !== 'person' && r.kind !== 'lead') return '';
  const parts = r.name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase();
}

/**
 * "Escalates to" text of an overdue open item: the named person, else the role
 * that takes over when nobody is left to escalate to (Administration). Empty
 * when the item names neither.
 */
export function escalationTargetLabel(
  i18n: Pick<I18n, '_'>,
  item: Pick<WorkflowItemResponse, 'escalation_target_name' | 'escalation_target_role'>,
): string | undefined {
  if (item.escalation_target_name) return item.escalation_target_name;
  if (item.escalation_target_role) return assignedRoleLabel(i18n, item.escalation_target_role) ?? undefined;
  return undefined;
}

/** What the current user may do with an item. The server enforces the same rules. */
export interface WorkflowItemPermissions {
  /** The current user leads the group the item belongs to. */
  isGroupLead: boolean;
  /** May hand the item to any employee (admin or group lead). */
  canHandToAnyone: boolean;
  /** May take the person off the item. */
  canRemove: boolean;
  /** May send a reminder to the holder (admin or group lead). */
  canRemind: boolean;
}

/**
 * Admin: everything. Group lead of the item: hand to anyone, remove, remind.
 * Anyone else: take the item or hand it to the group lead, and remove the
 * person only when they hold it themselves; no reminders.
 */
export function workflowItemPermissions(
  item: Pick<WorkflowItemResponse, 'assigned_to' | 'group_lead_clerk_user_id'>,
  currentUserId: string | null | undefined,
  isAdmin: boolean,
): WorkflowItemPermissions {
  const isGroupLead = !!currentUserId && !!item.group_lead_clerk_user_id && item.group_lead_clerk_user_id === currentUserId;
  const holdsIt = !!currentUserId && !!item.assigned_to && item.assigned_to === currentUserId;
  const privileged = isAdmin || isGroupLead;
  return {
    isGroupLead,
    canHandToAnyone: privileged,
    canRemove: privileged || holdsIt,
    canRemind: privileged,
  };
}
